/**
 * What "last 30 days" resolves to, decided once.
 *
 * The timeline, both charts, the CSV export and the PDF report all ask this
 * question, and they have to agree — an export headed "last 30 days" that
 * covers 31 is a quiet lie, and one that covers a different 30 days from the
 * chart it was generated beside is worse.
 *
 * Boundaries are local start and end of day in the viewer's zone, never UTC
 * midnights. In London in summer a UTC midnight is 01:00, so a UTC-bounded
 * "today" silently drops the first hour of it and picks up an hour of
 * yesterday.
 */

import { err, invalid, ok } from '../model/result.js'
import type { Result } from '../model/result.js'
import type { Timestamp, WallClock } from '../model/timestamp.js'
import { epochFromWallClock, wallClockIn } from '../model/timestamp.js'
import type { Clock } from '../ports/clock.js'
import type { EpochRange } from '../ports/storage.js'

export type RangePreset =
  'last7' | 'last30' | 'last90' | 'thisMonth' | 'lastMonth' | 'allTime'

export const RANGE_PRESETS: readonly RangePreset[] = [
  'last7',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
  'allTime',
]

export function presetLabel(preset: RangePreset): string {
  switch (preset) {
    case 'last7':
      return 'Last 7 days'
    case 'last30':
      return 'Last 30 days'
    case 'last90':
      return 'Last 90 days'
    case 'thisMonth':
      return 'This month'
    case 'lastMonth':
      return 'Last month'
    case 'allTime':
      return 'All time'
  }
}

export type RangeSelection =
  | { readonly kind: 'preset'; readonly preset: RangePreset }
  /** Inclusive local days, `YYYY-MM-DD`. */
  | { readonly kind: 'custom'; readonly fromDay: string; readonly toDay: string }

export interface ResolvedRange extends EpochRange {
  readonly fromDay: string
  readonly toDay: string
  /**
   * What the selection was called — `Last 30 days`, or `Custom`.
   *
   * Carried alongside the absolute days rather than instead of them. A report
   * headed "last 30 days" is meaningless six weeks after it was printed; one
   * headed "1 – 30 June 2026" is a record, and T-504 needs both.
   */
  readonly label: string
  readonly selection: RangeSelection
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function pad(value: number, width = 2): string {
  return String(Math.abs(value)).padStart(width, '0')
}

function dayOf(wall: WallClock): string {
  return `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}`
}

function parseDay(day: string): WallClock | null {
  if (!DAY_PATTERN.test(day)) {
    return null
  }
  const year = Number(day.slice(0, 4))
  const month = Number(day.slice(5, 7))
  const date = Number(day.slice(8, 10))
  if (month < 1 || month > 12 || date < 1 || date > 31) {
    return null
  }
  // Reject 31 February rather than letting Date roll it into March.
  const rolled = new Date(Date.UTC(year, month - 1, date))
  if (rolled.getUTCMonth() + 1 !== month || rolled.getUTCDate() !== date) {
    return null
  }
  return { year, month, day: date, hour: 0, minute: 0, second: 0 }
}

/** Midnight beginning a local day. */
function startOf(day: WallClock, timeZone: string): number {
  return epochFromWallClock({ ...day, hour: 0, minute: 0, second: 0 }, timeZone)
}

/** The last millisecond of a local day, so the whole of it is included. */
function endOf(day: WallClock, timeZone: string): number {
  return epochFromWallClock({ ...day, hour: 23, minute: 59, second: 59 }, timeZone) + 999
}

function shiftDays(wall: WallClock, days: number): WallClock {
  const shifted = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days))
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: 0,
    minute: 0,
    second: 0,
  }
}

export interface ResolveRangeOptions {
  readonly clock: Clock
  /**
   * The first and last entry in the database, for `allTime`.
   *
   * Absent means there is nothing recorded, which is not an error — it resolves
   * to today, an ordinary range that happens to contain no data, so every
   * consumer takes the empty-range path it already has rather than a special
   * one it would only exercise on a fresh install.
   */
  readonly bounds?: { readonly first?: Timestamp; readonly last?: Timestamp }
}

/** Checks a selection before it is applied, without applying it. */
export function validateRangeSelection(
  selection: RangeSelection,
): Result<RangeSelection> {
  if (selection.kind === 'preset') {
    return ok(selection)
  }

  const from = parseDay(selection.fromDay)
  const to = parseDay(selection.toDay)
  const errors = []
  if (from === null) {
    errors.push(invalid('range.from.invalid', 'That is not a date.', 'fromDay'))
  }
  if (to === null) {
    errors.push(invalid('range.to.invalid', 'That is not a date.', 'toDay'))
  }
  // Refused rather than swapped: silently reversing the dates hides a typo in
  // the field that caused it, and the user sees a range they did not ask for
  // and cannot tell why.
  if (from !== null && to !== null && selection.toDay < selection.fromDay) {
    errors.push(
      invalid('range.inverted', 'The end date is before the start date.', 'toDay'),
    )
  }
  return errors.length > 0 ? err<RangeSelection>(...errors) : ok(selection)
}

/**
 * Resolves a selection to absolute instants in the viewer's current zone.
 *
 * An invalid custom selection falls back to today rather than throwing —
 * `validateRangeSelection` is where a bad range is reported, and this is called
 * from render paths that have no way to show an error.
 */
export function resolveRange(
  selection: RangeSelection,
  options: ResolveRangeOptions,
): ResolvedRange {
  const now = options.clock.now()
  const zone = options.clock.timeZone()
  const today = wallClockIn(now.epochMs, zone)

  const build = (
    from: WallClock,
    to: WallClock,
    label: string,
    options: {
      readonly selection?: RangeSelection
      /** Defaults to the viewer's zone; `allTime` uses the entries' own. */
      readonly fromZone?: string
      readonly toZone?: string
    } = {},
  ): ResolvedRange => ({
    fromEpochMs: startOf(from, options.fromZone ?? zone),
    toEpochMs: endOf(to, options.toZone ?? zone),
    fromDay: dayOf(from),
    toDay: dayOf(to),
    label,
    selection: options.selection ?? selection,
  })

  if (selection.kind === 'custom') {
    const from = parseDay(selection.fromDay)
    const to = parseDay(selection.toDay)
    if (from === null || to === null || selection.toDay < selection.fromDay) {
      return build(today, today, 'Custom', {
        selection: { kind: 'preset', preset: 'last7' },
      })
    }
    return build(from, to, 'Custom')
  }

  switch (selection.preset) {
    case 'last7':
      // Inclusive of today, so "last 7 days" is seven days on a calendar and
      // not eight.
      return build(shiftDays(today, -6), today, presetLabel('last7'))
    case 'last30':
      return build(shiftDays(today, -29), today, presetLabel('last30'))
    case 'last90':
      return build(shiftDays(today, -89), today, presetLabel('last90'))
    case 'thisMonth':
      return build({ ...today, day: 1 }, today, presetLabel('thisMonth'))
    case 'lastMonth': {
      const lastDayOfPrevious = shiftDays({ ...today, day: 1 }, -1)
      return build(
        { ...lastDayOfPrevious, day: 1 },
        lastDayOfPrevious,
        presetLabel('lastMonth'),
      )
    }
    case 'allTime': {
      const first = options.bounds?.first
      const last = options.bounds?.last
      if (first === undefined || last === undefined) {
        return build(today, today, presetLabel('allTime'))
      }
      /*
       * Resolved in each entry's **own** zone, unlike every other preset.
       *
       * "All time" means from the start of the first entry's day to the end of
       * the last one's, and which day those are is decided by where the user
       * was — the same rule the timeline groups by. Using the device's zone
       * instead labels an entry logged at 23:30 in New York as the following
       * day, so the range control and the day header disagree about the one
       * entry in the database.
       *
       * It also cannot exclude what it is defined by: the earliest instant of
       * the first entry's local day is necessarily at or before that entry.
       */
      return build(
        wallClockIn(first.epochMs, first.timeZone),
        wallClockIn(last.epochMs, last.timeZone),
        presetLabel('allTime'),
        { fromZone: first.timeZone, toZone: last.timeZone },
      )
    }
  }
}

/** Whether a resolved range contains any of these instants. */
export function rangeContains(range: EpochRange, at: Timestamp): boolean {
  return at.epochMs >= range.fromEpochMs && at.epochMs <= range.toEpochMs
}

/** Whole days spanned by a range, inclusive at both ends. */
export function rangeDays(range: ResolvedRange): number {
  const from = parseDay(range.fromDay)
  const to = parseDay(range.toDay)
  if (from === null || to === null) {
    return 0
  }
  const fromUtc = Date.UTC(from.year, from.month - 1, from.day)
  const toUtc = Date.UTC(to.year, to.month - 1, to.day)
  return Math.round((toUtc - fromUtc) / 86_400_000) + 1
}

/** `2026-07-28` back to a wall clock, for callers that format their own dates. */
export function dayToWallClock(day: string): WallClock | null {
  return parseDay(day)
}
